import BrandMark from "../components/brand/BrandMark";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Users, CreditCard, QrCode, LogOut, 
  BarChart3, Settings, Phone, Wifi, WifiOff, 
  MessageCircle, Clock, CheckCircle, XCircle,
  RefreshCw, Activity, FileText
} from "lucide-react";

const API = '/api';

const AdminTwilioStatus = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/twilio-status`);
      setStatus(response.data);
    } catch (error) {
      toast.error("Error al cargar estado de Twilio");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStatus();
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const StatusIndicator = ({ ok, label }) => (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle className="w-5 h-5 text-[#5BC0FF]" />
      ) : (
        <XCircle className="w-5 h-5 text-red-500" />
      )}
      <span className={ok ? "text-[#5BC0FF]" : "text-red-400"}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B2545]">
      {/* Sidebar */}


      {/* Mobile Header */}
      

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Estado de Twilio
              </h1>
              <p className="text-slate-400 mt-1">Monitorea los webhooks y la configuración de Twilio</p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-600 text-slate-300 hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : status ? (
            <div className="grid gap-6">
              {/* General Status */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Estado General
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Twilio Configurado</p>
                      <StatusIndicator 
                        ok={status.twilio_configured} 
                        label={status.twilio_configured ? "Sí" : "No"} 
                      />
                    </div>
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Modo</p>
                      <Badge className={status.is_production ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}>
                        {status.is_production ? "Producción" : "Desarrollo"}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Phone Numbers */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Números Configurados
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Números enmascarados por seguridad
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Caller ID (Voz)</p>
                      <p className="text-white font-mono">{status.caller_id || "No configurado"}</p>
                    </div>
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">WhatsApp</p>
                      <p className="text-white font-mono">{status.whatsapp_number || "No configurado"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Voice Webhook */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Webhook de Voz
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-slate-700 p-4 rounded-lg">
                    <p className="text-slate-400 text-sm mb-2">URL</p>
                    <code className="text-[#5BC0FF] text-sm break-all">{status.voice_webhook?.url}</code>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Estado</p>
                      <StatusIndicator 
                        ok={status.voice_webhook?.reachable} 
                        label={status.voice_webhook?.reachable ? "Alcanzable" : "No alcanzable"} 
                      />
                    </div>
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Secreto</p>
                      <StatusIndicator 
                        ok={status.voice_webhook?.secret_configured} 
                        label={status.voice_webhook?.secret_configured ? "Configurado" : "No configurado"} 
                      />
                    </div>
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Último recibido</p>
                      <p className="text-white text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-500" />
                        {status.voice_webhook?.last_received 
                          ? new Date(status.voice_webhook.last_received).toLocaleString('es-PE')
                          : "Nunca"
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp Webhook */}
              <Card className="bg-[#14375F] border-white/15">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    Webhook de WhatsApp
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-slate-700 p-4 rounded-lg">
                    <p className="text-slate-400 text-sm mb-2">URL</p>
                    <code className="text-[#5BC0FF] text-sm break-all">{status.whatsapp_webhook?.url}</code>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Estado</p>
                      <StatusIndicator 
                        ok={status.whatsapp_webhook?.reachable} 
                        label={status.whatsapp_webhook?.reachable ? "Alcanzable" : "No alcanzable"} 
                      />
                    </div>
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Secreto</p>
                      <StatusIndicator 
                        ok={status.whatsapp_webhook?.secret_configured} 
                        label={status.whatsapp_webhook?.secret_configured ? "Configurado" : "No configurado"} 
                      />
                    </div>
                    <div className="bg-slate-700 p-4 rounded-lg">
                      <p className="text-slate-400 text-sm mb-2">Último recibido</p>
                      <p className="text-white text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-500" />
                        {status.whatsapp_webhook?.last_received 
                          ? new Date(status.whatsapp_webhook.last_received).toLocaleString('es-PE')
                          : "Nunca"
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Configuration Instructions */}
              <Card className="bg-amber-500/10 border-amber-500/30">
                <CardContent className="p-6">
                  <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Configuración en Twilio Console
                  </h3>
                  <div className="space-y-3 text-slate-300 text-sm">
                    <p>Para que los webhooks funcionen en producción, configura las siguientes URLs en la consola de Twilio:</p>
                    <div className="bg-[#14375F] p-3 rounded-lg">
                      <p className="text-slate-400 text-xs mb-1">Voice Webhook (Phone Number → Configure → Voice & Fax)</p>
                      <code className="text-[#5BC0FF]">{status.voice_webhook?.url}</code>
                    </div>
                    <div className="bg-[#14375F] p-3 rounded-lg">
                      <p className="text-slate-400 text-xs mb-1">WhatsApp Webhook (Messaging → WhatsApp Sandbox → When a message comes in)</p>
                      <code className="text-[#5BC0FF]">{status.whatsapp_webhook?.url}</code>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="bg-[#14375F] border-white/15">
              <CardContent className="p-12 text-center">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-slate-400">No se pudo cargar el estado de Twilio</p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminTwilioStatus;
